import { useEffect, useRef, useState } from 'react';
import type { SessionInfo, UserProfile } from '../types/api';
import { Button, Field, Icon, Menu, MenuItem, Modal, SectionLabel, TextInput } from '../ui';
import InviteDialog from './InviteDialog';
import SettingsMenu from './SettingsMenu';
import settingsMenuStyles from './SettingsMenu.module.css';
import styles from './Sidebar.module.css';

interface Props {
  sessions: SessionInfo[];
  sessionsLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  currentSessionId: string | null;
  collapsed: boolean;
  width: number;
  onToggle: () => void;
  onWidthChange: (w: number) => void;
  onNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
  onRenameSession: (id: string, title: string) => Promise<void>;
  onDeleteSession: (id: string) => Promise<void>;
  onSearch: (q: string) => void;
  onLoadMore: () => void;
  user: UserProfile | null;
  /** The menu's What's new item. Absent when this person has no notes to read. */
  whatsNew?: { unread: boolean; onOpen: () => void };
}

interface SessionGroup {
  label: string;
  sessions: SessionInfo[];
}

function groupByDate(sessions: SessionInfo[]): SessionGroup[] {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86_400_000;
  const weekStart = todayStart - 7 * 86_400_000;

  const groups: SessionGroup[] = [
    { label: 'Today', sessions: [] },
    { label: 'Yesterday', sessions: [] },
    { label: 'Previous 7 days', sessions: [] },
    { label: 'Older', sessions: [] },
  ];

  for (const s of sessions) {
    const t = new Date(s.updated_at).getTime();
    if (t >= todayStart) groups[0].sessions.push(s);
    else if (t >= yesterdayStart) groups[1].sessions.push(s);
    else if (t >= weekStart) groups[2].sessions.push(s);
    else groups[3].sessions.push(s);
  }

  return groups.filter((g) => g.sessions.length > 0);
}

function sessionLabel(s: SessionInfo): string {
  if (s.title) return s.title;
  const d = new Date(s.created_at);
  return `Chat · ${d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}`;
}

/**
 * A DHI address is already the person's initials (sifd@dhigroup.com), so it is used verbatim.
 * Four letters is the most the 30px avatar can hold, and only just: measured in the real box,
 * SIFD is 27.6px and a five-glyph alias like sifd2 is 34.3px, so a numbered alias deliberately
 * falls through to two-letter initials rather than overflowing.
 */
const INITIALS_ALIAS = /^[a-z]{2,4}$/i;

/** One letter per word, treating the separators an address uses as word breaks too. */
function initialsOf(source: string): string {
  return (
    source
      .split(/[\s._+-]+/)
      .filter(Boolean)
      // Array.from, so a word opening with an emoji or other astral-plane character
      // contributes that character rather than half of a surrogate pair.
      .map((w) => Array.from(w)[0].toUpperCase())
      .slice(0, 2)
      .join('')
  );
}

export function getInitials(name: string, email?: string): string {
  const local = email?.split('@')[0] ?? '';
  if (INITIALS_ALIAS.test(local)) return local.toUpperCase();
  // Anything else (sina.farhangdoust@gmail.com) is a name, not an alias. A name that
  // yields no letters at all hands over to the address before giving up.
  return initialsOf(name.trim()) || initialsOf(local) || '?';
}

export default function Sidebar({
  sessions,
  sessionsLoading,
  loadingMore,
  hasMore,
  currentSessionId,
  collapsed,
  width,
  onToggle,
  onWidthChange,
  onNewChat,
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  onSearch,
  onLoadMore,
  user,
  whatsNew,
}: Props) {
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // Rendered for organisation admins only, from the role /me reports. The backend checks
  // for itself on every invite call, so this decides what is shown and nothing more.
  const isAdmin = user?.role === 'admin';
  const [inviteOpen, setInviteOpen] = useState(false);

  const [renameModal, setRenameModal] = useState<SessionInfo | null>(null);
  const [renameInput, setRenameInput] = useState('');
  const [deleteModal, setDeleteModal] = useState<SessionInfo | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const [searchInput, setSearchInput] = useState('');
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => onSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, onSearch]);

  // infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelEl || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { rootMargin: '80px' },
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl, hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      // Cap against the viewport as well as the absolute 440px. The sidebar is
      // flex-shrink: 0 and sets min-width inline, so an over-wide sidebar cannot
      // be squeezed later — it comes out of the chat column instead, which is the
      // only flexible element in the row. 25vw keeps the conversation readable at
      // laptop widths; above ~1760px the 440px cap is the binding one, as before.
      const maxW = Math.min(440, Math.round(window.innerWidth * 0.25));
      const newW = Math.max(180, Math.min(maxW, startWidth.current + (e.clientX - startX.current)));
      onWidthChange(newW);
    };
    const handleUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [onWidthChange]);

  // Focus and select the rename input when the dialog opens. The dialog element
  // does put focus inside itself, but on the first focusable child, which is the
  // header's close button; and nothing native selects the existing title, which
  // is what makes typing a replacement one keystroke rather than a select-all.
  useEffect(() => {
    if (renameModal) {
      setTimeout(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      }, 50);
    }
  }, [renameModal]);

  const handleResizeStart = (e: React.MouseEvent) => {
    if (collapsed) return;
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleRenameClick = (s: SessionInfo) => {
    setRenameInput(s.title ?? sessionLabel(s));
    setRenameModal(s);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRenameConfirm = async () => {
    if (!renameModal || !renameInput.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onRenameSession(renameModal.session_id, renameInput.trim());
      setRenameModal(null);
    } catch (err) {
      console.error('Failed to rename session:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onDeleteSession(deleteModal.session_id);
      setDeleteModal(null);
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sidebarStyle = {
    width: collapsed ? 'var(--collapsed-w)' : `${width}px`,
    minWidth: collapsed ? 'var(--collapsed-w)' : `${width}px`,
    transition: isResizing.current
      ? 'none'
      : 'width 260ms cubic-bezier(0.4, 0, 0.2, 1), min-width 260ms cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const groups = groupByDate(sessions);

  const userName = user?.name || 'Guest User';
  const userEmail = user?.email || '';

  return (
    <aside className={styles.sidebar} style={sidebarStyle}>
      {!collapsed && (
        // biome-ignore lint/a11y/noStaticElementInteractions: drag handle is pointer-only; sidebar width also persists across sessions so keyboard users are not blocked
        <div className={styles.resizeHandle} onMouseDown={handleResizeStart} />
      )}

      {/* Header */}
      <div className={`${styles.header} ${collapsed ? styles.headerCollapsed : ''}`}>
        {!collapsed && (
          <>
            <img
              className={styles.dhiMark}
              src={`${import.meta.env.BASE_URL}DHI_Logo_Neg_White_noMargin.png`}
              alt="DHI"
            />
            <div className={styles.wordmarkGroup}>
              <button
                className={styles.wordmarkBtn}
                type="button"
                onClick={onNewChat}
                title="Go to home"
              >
                <span className={styles.wordmark}>
                  Docs<em className={styles.wordmarkAccent}> AI</em>
                </span>
              </button>
              <span className={styles.betaTag}>Beta</span>
            </div>
          </>
        )}
        <button
          className={`${styles.toggleBtn} ${collapsed ? styles.toggleBtnCollapsed : ''}`}
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={14} />
        </button>
      </div>

      {/* New chat */}
      <button
        className={`${styles.newChatBtn} ${collapsed ? styles.newChatBtnCollapsed : ''}`}
        onClick={onNewChat}
        type="button"
        title="New conversation"
      >
        <Icon name="plus" size={13} className={styles.newChatIcon} />
        {!collapsed && <span className={styles.btnLabel}>New conversation</span>}
      </button>

      {/* Invite colleagues: organisation admins only */}
      {isAdmin && (
        <button
          className={`${styles.inviteBtn} ${collapsed ? styles.newChatBtnCollapsed : ''}`}
          onClick={() => setInviteOpen(true)}
          type="button"
          title="Invite colleagues"
        >
          <Icon name="user-plus" size={13} />
          {!collapsed && <span className={styles.btnLabel}>Invite colleagues</span>}
        </button>
      )}

      {/* Search */}
      {!collapsed && (
        <div className={styles.searchWrapper}>
          <Icon name="search" size={13} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search conversations…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className={styles.searchClear}
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
            >
              <Icon name="close" size={10} />
            </button>
          )}
        </div>
      )}

      {/* Sessions */}
      {!collapsed && (
        <div className={styles.sessionsArea}>
          {sessionsLoading && sessions.length === 0 ? null : groups.length === 0 ? (
            <div className={styles.emptySearch}>No conversations found</div>
          ) : (
            groups.map((group) => (
              <div key={group.label}>
                <SectionLabel className={styles.groupLabel}>{group.label}</SectionLabel>
                {group.sessions.map((s) => {
                  const isActive = s.session_id === currentSessionId;
                  return (
                    <div
                      key={s.session_id}
                      className={`${styles.sessionItem} ${isActive ? styles.sessionItemActive : ''}`}
                    >
                      <button
                        className={styles.sessionBtn}
                        onClick={() => onSelectSession(s.session_id)}
                        type="button"
                        title={sessionLabel(s)}
                      >
                        {sessionLabel(s)}
                      </button>
                      <Menu
                        align="end"
                        className={styles.menuWrap}
                        trigger={(props) => (
                          <button
                            {...props}
                            className={styles.menuBtn}
                            type="button"
                            aria-label={`Actions for ${sessionLabel(s)}`}
                          >
                            <Icon name="more-horizontal" size={14} />
                          </button>
                        )}
                      >
                        <MenuItem icon="pencil" onSelect={() => handleRenameClick(s)}>
                          Rename
                        </MenuItem>
                        <MenuItem icon="trash" danger onSelect={() => setDeleteModal(s)}>
                          Delete
                        </MenuItem>
                      </Menu>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          {loadingMore && (
            <div className={styles.loadingMore}>
              <span className={styles.loadingDot} />
              <span className={styles.loadingDot} />
              <span className={styles.loadingDot} />
            </div>
          )}
          {hasMore && !loadingMore && <div ref={setSentinelEl} className={styles.sentinel} />}
        </div>
      )}

      {/* Footer */}
      <div className={`${styles.footer} ${collapsed ? styles.footerCollapsed : ''}`}>
        <div className={styles.avatarWrap}>
          <div className={styles.avatar}>{getInitials(userName, userEmail || undefined)}</div>
          {collapsed && whatsNew?.unread && <span className={styles.unreadDot} />}
        </div>
        {!collapsed && (
          <>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{userName}</div>
              <div className={styles.userEmail}>{userEmail}</div>
            </div>
            <div className={settingsMenuStyles.wrapper}>
              <button
                ref={menuTriggerRef}
                type="button"
                className={styles.settingsBtn}
                title={whatsNew?.unread ? "Menu (new in What's new)" : 'Menu'}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <Icon
                  name="chevron-up"
                  size={15}
                  className={menuOpen ? styles.menuChevronOpen : undefined}
                />
              </button>
              {whatsNew?.unread && <span className={styles.unreadDot} />}
              <SettingsMenu
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                triggerRef={menuTriggerRef}
                whatsNew={whatsNew}
              />
            </div>
          </>
        )}
      </div>

      {isAdmin && (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          organisation={user?.organisation ?? null}
          inviterEmail={user?.email ?? ''}
        />
      )}

      <Modal
        open={renameModal !== null}
        onClose={() => setRenameModal(null)}
        title="Rename conversation"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameModal(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRenameConfirm}
              loading={isSubmitting}
              disabled={!renameInput.trim()}
            >
              Save
            </Button>
          </>
        }
      >
        <Field label="Title">
          <TextInput
            ref={renameInputRef}
            value={renameInput}
            onChange={(e) => setRenameInput(e.target.value)}
            // Enter submits. Escape is not handled here: the dialog raises its
            // own cancel event for it, which is already wired to onClose.
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameConfirm();
            }}
            placeholder="Conversation title"
            maxLength={500}
          />
        </Field>
      </Modal>

      <Modal
        open={deleteModal !== null}
        onClose={() => setDeleteModal(null)}
        title="Delete conversation?"
        size="sm"
        description="This conversation will be permanently removed."
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteModal(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} loading={isSubmitting}>
              Delete
            </Button>
          </>
        }
      />
    </aside>
  );
}
